import React, { useEffect, useState, useRef } from 'react';
import {
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalCloseButton,
  ModalBody, ModalFooter, Button, VStack, Input, Box, Text, Spinner, useToast,
  Flex, Avatar, Badge, HStack, InputGroup, InputRightElement, Icon, useColorModeValue,
  Alert, AlertIcon, AlertTitle, AlertDescription, CloseButton
} from '@chakra-ui/react';
import { FiSend, FiPaperclip, FiCheckCircle, FiAlertTriangle } from 'react-icons/fi';
import { checkProfanity } from '../../utils/profanityFilter';

const API_BASE_URL =
  process.env.NODE_ENV === 'production'
    ? process.env.REACT_APP_VERCEL_API_URL
    : process.env.REACT_APP_LOCAL_API_URL;

// Component for message bubbles
const MessageBubble = ({ message, isAdmin, username }) => {
  const adminBg = useColorModeValue('blue.100', 'blue.700');
  const userBg = useColorModeValue('green.100', 'green.700');
  const timestampColor = useColorModeValue('gray.500', 'gray.400');
  
  return (
    <Flex
      direction="column"
      alignSelf={isAdmin ? 'flex-end' : 'flex-start'}
      maxW="70%"
    >
      <Flex align="center" mb={1} gap={2}>
        <Avatar 
          size="xs" 
          name={isAdmin ? 'Admin' : message.senderName || username} 
          bg={isAdmin ? 'blue.500' : 'green.500'}
        />
        <Text fontWeight="bold" fontSize="sm">
          {isAdmin ? 'Suporte' : message.senderName || username}
        </Text>
      </Flex>
      
      <Box
        bg={isAdmin ? adminBg : userBg}
        p={3}
        borderRadius="lg"
        boxShadow="sm"
        position="relative"
      >
        <Text>{message.content}</Text>
        <Flex justify="space-between" align="center" mt={2}>
          <Text fontSize="xs" color={timestampColor}>
            {formatTime(message.timestamp)}
          </Text>
          {isAdmin && (
            <Icon 
              as={FiCheckCircle} 
              color="green.500" 
              boxSize="12px"
            />
          )}
        </Flex>
      </Box>
    </Flex>
  );
};

// Component for message input
const MessageInput = ({ newMessage, setNewMessage, handleSendMessage, inputRef, isSending }) => {
  const inputBg = useColorModeValue('white', 'gray.700');
  
  return (
    <Flex w="100%">
      <InputGroup size="md">
        <Input
          ref={inputRef}
          placeholder="Digite sua mensagem..."
          value={newMessage}
          onChange={(e) => setNewMessage(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && !isSending && handleSendMessage()}
          bg={inputBg}
          borderRadius="md"
        />
        <InputRightElement width="4.5rem">
          <Button 
            h="1.75rem" 
            size="sm" 
            colorScheme="blue" 
            onClick={handleSendMessage}
            isLoading={isSending}
            isDisabled={isSending}
          >
            <Icon as={FiSend} />
          </Button>
        </InputRightElement>
      </InputGroup>
      <Button
        ml={2}
        variant="ghost"
        colorScheme="blue"
        aria-label="Attach file"
      >
        <Icon as={FiPaperclip} />
      </Button>
    </Flex>
  );
};

// Utility function to format timestamp
const formatTime = (timestamp) => {
  const date = new Date(timestamp);
  const today = new Date();
  const isToday = date.getDate() === today.getDate() && 
                 date.getMonth() === today.getMonth() &&
                 date.getFullYear() === today.getFullYear();
                 
  if (isToday) {
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }
  
  return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

// Main component
const AdminChatModal = ({ userId, isOpen, onClose, userName = 'Usuário' }) => {
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [profanityWarning, setProfanityWarning] = useState({ show: false, words: [] });
  const toast = useToast();
  const bottomRef = useRef();
  const inputRef = useRef();
  const scrollContainerRef = useRef();
  
  // Color scheme based on color mode
  const bgColor = useColorModeValue('gray.50', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const headerBg = useColorModeValue('blue.500', 'blue.700');
  const headerColor = useColorModeValue('white', 'white');
  const warningBg = useColorModeValue('red.50', 'red.900');

  // Fetch messages when modal opens
  useEffect(() => {
    const token = localStorage.getItem('userToken_TOVIAPPS');  
    if (!isOpen || !userId || !token) {
      return;
    }
  
    const fetchMessages = async () => {
      setLoading(true);
  
      try {
        const response = await fetch(`${API_BASE_URL}api/adminConverations/admin/${userId}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });
  
        if (!response.ok) throw new Error('Failed to load messages');
        const data = await response.json();
        setMessages(data.messages || []);
      } catch (err) {
        toast({
          title: 'Erro',
          description: 'Falha ao carregar mensagens do chat.',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      } finally {
        setLoading(false);
      }
    };
  
    fetchMessages();
  }, [isOpen, userId, toast]);
  
  // Focus input when modal opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => inputRef.current.focus(), 100);
    }
  }, [isOpen]);

  // Scroll to bottom on message load/update
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Check for profanity in the input
  useEffect(() => {
    if (newMessage) {
      const { hasProfanity, words } = checkProfanity(newMessage);
      
      if (hasProfanity) {
        setProfanityWarning({ show: true, words });
      } else {
        setProfanityWarning({ show: false, words: [] });
      }
    } else {
      setProfanityWarning({ show: false, words: [] });
    }
  }, [newMessage]);

  // Handle sending new message
  const handleSendMessage = async () => {
    if (!newMessage.trim() || isSending) return;
    
    // Check for profanity before sending
    const { hasProfanity, words } = checkProfanity(newMessage);
    
    if (hasProfanity) {
      setProfanityWarning({ show: true, words });
      toast({
        title: 'Mensagem bloqueada',
        description: 'A mensagem contém palavras inadequadas e não pode ser enviada.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      return;
    }
    
    setIsSending(true);

    try {
      const token = localStorage.getItem('userToken_TOVIAPPS');
      const response = await fetch(`${API_BASE_URL}api/adminConverations/admin/${userId}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          content: newMessage,
        }),
      });

      if (!response.ok) throw new Error('Erro ao enviar mensagem');

      const data = await response.json();
      setMessages((prev) => [...prev, data.message]);
      setNewMessage('');
      setProfanityWarning({ show: false, words: [] });
    } catch (err) {
      toast({
        title: 'Erro',
        description: 'Não foi possível enviar a mensagem.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSending(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <ModalOverlay backdropFilter="blur(5px)" />
      <ModalContent borderRadius="md" overflow="hidden" maxH="90vh" h="600px" display="flex" flexDirection="column">
        <ModalHeader 
          bg={headerBg} 
          color={headerColor}
          py={3}
          display="flex"
          alignItems="center"
          justifyContent="space-between"
        >
          <Flex align="center">
            <Avatar size="sm" name={userName} mr={2} />
            <Box>
              <Text>Chat com {userName}</Text>
              <Badge size="sm" colorScheme="green">Online</Badge>
            </Box>
          </Flex>
          <ModalCloseButton position="static" color="white" />
        </ModalHeader>

        <ModalBody 
          bg={bgColor} 
          p={4} 
          flex="1" 
          overflow="hidden" 
          display="flex" 
          flexDirection="column"
        >
          {/* Profanity Warning Alert */}
          {profanityWarning.show && (
            <Alert status="error" mb={3} borderRadius="md" bg={warningBg}>
              <AlertIcon as={FiAlertTriangle} />
              <Box flex="1">
                <AlertTitle fontSize="sm">Conteúdo inadequado detectado</AlertTitle>
                <AlertDescription fontSize="xs">
                  Sua mensagem contém palavras inadequadas e não pode ser enviada.
                  Por favor, revise seu texto.
                </AlertDescription>
              </Box>
              <CloseButton 
                size="sm" 
                onClick={() => setProfanityWarning({ show: false, words: [] })} 
              />
            </Alert>
          )}
          
          <Box
            ref={scrollContainerRef}
            flex="1"
            overflowY="auto"
            p={3}
            border="1px solid"
            borderColor={borderColor}
            borderRadius="md"
            bg={bgColor}
            css={{
              '&::-webkit-scrollbar': {
                width: '8px',
              },
              '&::-webkit-scrollbar-track': {
                background: useColorModeValue('gray.100', 'gray.800'),
                borderRadius: '8px',
              },
              '&::-webkit-scrollbar-thumb': {
                background: useColorModeValue('blue.200', 'blue.700'),
                borderRadius: '8px',
              },
            }}
          >
            {loading ? (
              <Flex justify="center" align="center" h="100%">
                <Spinner size="xl" color="blue.500" thickness="4px" />
              </Flex>
            ) : (
              <VStack spacing={4} align="stretch">
                {messages.length === 0 && (
                  <Flex justify="center" h="100px" align="center">
                    <Text color="gray.500">Nenhuma mensagem encontrada. Inicie uma conversa!</Text>
                  </Flex>
                )}
                
                {messages.map((msg, index) => {
                  const isAdmin = msg.sender === null;
                  return (
                    <MessageBubble 
                      key={index}
                      message={msg}
                      isAdmin={isAdmin}
                      username={userName}
                    />
                  );
                })}
                <div ref={bottomRef}></div>
              </VStack>
            )}
          </Box>
        </ModalBody>
        
        <ModalFooter bg={bgColor} borderTop="1px solid" borderColor={borderColor} p={3}>
          <MessageInput 
            newMessage={newMessage} 
            setNewMessage={setNewMessage} 
            handleSendMessage={handleSendMessage}
            inputRef={inputRef}
            isSending={isSending}
          />
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default AdminChatModal;